/**
 * Orders seasons (or rosters) most-recent-first.
 *
 * Keyed on `starts_at` descending to match the web app, which sorts the season
 * dropdown this way in `useGetScheduleFiltering` and then auto-selects index 0.
 * The Team screen's Schedule, Statistics and Roster tabs all share this
 * comparator so the three tabs open on the same season - sorting one of them
 * differently (e.g. on `ends_at`) makes them disagree whenever a season and its
 * playoffs start in one order but finish in another.
 *
 * Entries with no `starts_at` sort last.
 */

export interface DatedScheduleLike {
  starts_at?: string | null
}

const startsAtTime = (item: DatedScheduleLike): number => {
  if (!item?.starts_at) {
    return -Infinity
  }

  const time = new Date(item.starts_at).getTime()

  // Guard against unparseable dates, which would otherwise make the
  // comparator return NaN and leave the order undefined.
  return Number.isNaN(time) ? -Infinity : time
}

/** Comparator: most recent first. */
export const compareSeasonsByRecency = (
  a: DatedScheduleLike,
  b: DatedScheduleLike
): number => {
  const aTime = startsAtTime(a)
  const bTime = startsAtTime(b)

  if (aTime === bTime) {
    return 0
  }

  return aTime > bTime ? -1 : 1
}

/**
 * Sorts in place (like the web implementation) and returns the same array so
 * the result can be used inline.
 */
export const sortSeasonsByRecency = <T extends DatedScheduleLike>(
  items: T[] | null | undefined
): T[] => {
  if (!Array.isArray(items)) {
    return []
  }

  return items.sort(compareSeasonsByRecency)
}
